/**
 * useUserForm - Hook for UserFormModal
 *
 * Manages:
 * - Form state and initialization (create/edit modes)
 * - CPF and telefone input masks
 * - Password visibility and strength validation
 * - Data loading (hospitals list)
 * - Submit handler (create/update)
 * - Zod validation schemas
 */
import React, { useState, useEffect } from 'react';
import { z } from 'zod';
import userService from '../services/userService';
import { validarCPF, mascaraCPF, mascaraTelefone, validarTelefone } from '../lib/utils';
import { msg } from '../utils/messages';
import { validatePassword, getStrengthColor } from '../services/passwordService';
import { ROLE_OPTIONS, ROLES_LIST, type Role } from '../constants/roles';

const userCreateSchema = z.object({
  username: z.string().min(3, 'Username deve ter no mínimo 3 caracteres'),
  full_name: z.string().min(3, 'Nome completo deve ter no mínimo 3 caracteres'),
  email: z.string().email('Email inválido'),
  password: z.string().min(8, 'Senha deve ter no mínimo 8 caracteres (NIST)').max(64, 'Senha deve ter no máximo 64 caracteres (NIST)'),
  confirmPassword: z.string().min(8, 'Confirmação de senha deve ter no mínimo 8 caracteres'),
  role: z.enum(ROLES_LIST as [Role, ...Role[]], { errorMap: () => ({ message: 'Perfil é obrigatório' }) }),
  cpf: z.string().optional().refine((val) => !val || validarCPF(val), 'CPF inválido. Use o formato: 000.000.000-00'),
  hospital_id: z.number().optional(),
  telefone: z.string().optional().refine((val) => !val || validarTelefone(val), 'Telefone inválido. Use (XX) XXXXX-XXXX ou (XX) XXXX-XXXX'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'As senhas não coincidem',
  path: ['confirmPassword'],
});

const userUpdateSchema = z.object({
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  email: z.string().email('Email inválido').optional(),
  role: z.enum(ROLES_LIST as [Role, ...Role[]]).optional(),
  cpf: z.string().optional().refine((val) => !val || validarCPF(val), 'CPF inválido. Use o formato: 000.000.000-00'),
  hospital_id: z.number().optional(),
  telefone: z.string().optional().refine((val) => !val || validarTelefone(val), 'Telefone inválido. Use (XX) XXXXX-XXXX ou (XX) XXXX-XXXX'),
  ativo: z.boolean().optional(),
});

export { ROLE_OPTIONS, getStrengthColor };

interface UseUserFormProps {
  user?: any | null;
  onClose: (shouldReload: boolean) => void;
  onSuccess: (message: string) => void;
}

export function useUserForm({ user, onClose, onSuccess }: UseUserFormProps) {
  const [formData, setFormData] = useState({
    username: user?.username || '',
    full_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    password: '',
    confirmPassword: '',
    role: user?.role || '',
    cpf: user?.cpf || '',
    hospital_id: user?.hospital_id || '',
    telefone: user?.telefone || '',
    ativo: user?.ativo !== undefined ? user.ativo : true,
  });

  const [hospitals, setHospitals] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: 'Fraca' });
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoadingData(true);
        const hospitalsData = await userService.getHospitals();
        setHospitals(hospitalsData);
      } catch (error: any) {
        console.error('Erro ao carregar dados:', error);
        msg.error('Erro ao carregar dados do formulário');
      } finally {
        setLoadingData(false);
      }
    };
    loadInitialData();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'cpf') {
      setFormData(prev => ({ ...prev, [name]: mascaraCPF(value) }));
    } else if (name === 'telefone') {
      setFormData(prev => ({ ...prev, [name]: mascaraTelefone(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Real-time password strength validation (CREATE mode only)
    if (name === 'password' && !user && value.length > 0) {
      validatePassword(value)
        .then(result => {
          setPasswordStrength({ score: result.strength_score, label: result.strength_label });
          setPasswordErrors(result.errors);
        })
        .catch(() => {
          setPasswordStrength({ score: 0, label: 'Fraca' });
          setPasswordErrors(['Erro ao validar senha']);
        });
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {
      if (user) {
        const dataToValidate = {
          first_name: formData.first_name || undefined,
          last_name: formData.last_name || undefined,
          email: formData.email || undefined,
          role: formData.role || undefined,
          cpf: formData.cpf || undefined,
          hospital_id: formData.hospital_id ? Number(formData.hospital_id) : undefined,
          telefone: formData.telefone || undefined,
          ativo: formData.ativo,
        };
        userUpdateSchema.parse(dataToValidate);
        setLoading(true);
        await userService.updateUser(user.id, dataToValidate);
        onSuccess('Usuário atualizado com sucesso');
        onClose(true);
      } else {
        const dataToValidate = {
          username: formData.username,
          full_name: formData.full_name,
          email: formData.email,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          role: formData.role as Role,
          cpf: formData.cpf || undefined,
          hospital_id: formData.hospital_id ? Number(formData.hospital_id) : undefined,
          telefone: formData.telefone || undefined,
        };
        userCreateSchema.parse(dataToValidate);
        const { confirmPassword, ...dataToSend } = dataToValidate;
        setLoading(true);
        await userService.createUser(dataToSend);
        onSuccess('Usuário criado com sucesso');
        onClose(true);
      }
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) fieldErrors[err.path[0].toString()] = err.message;
        });
        setErrors(fieldErrors);
      } else {
        console.error('Erro ao salvar usuário:', error);
        msg.error(error.response?.data?.message || error.response?.data?.detail || 'Erro ao salvar usuário');
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    hospitals,
    loading,
    loadingData,
    errors,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    passwordStrength,
    passwordErrors,
    handleChange,
    handleSubmit,
  };
}
